import { useIntl } from 'react-intl';
import { CheckSquare, HelpCircle } from 'react-feather';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  FormInstance,
  Typography,
  Space,
  Modal,
  Divider,
  Alert,
  Button,
  Statistic,
} from 'antd';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import {
  BankAccountInfiniteSelectField,
  CompaniesInfiniteSelectField,
  ContentWrapper,
  DocumentTypesInfiniteSelectField,
  Group,
  InputDate,
  InputDecimal,
  PlanAccountsSelect,
} from '@/components/_common';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import {
  AccountReceivableDebtor,
  AccountReceivableFormValues,
  AccountReceivableSituation,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import CustomerInputSearch from '@/components/_common/CustomerInputSearch';
import InsurerInputSearch from '@/components/_common/InsurerInputSearch';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import AccountReceivableHeader from '../AccountReceivableHeader';
import Authorities from '@/types/Authority';
import moment from 'moment';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';
import { useAccountReceivableContext } from '@/pages/FinancialPages/BillToReceivePage/ReleasesPage/ReleasesDetailPage';
import useGetInstallmentRate from '@/hooks/useGetInstallmentRate';
import AccountReceivableObservations from '../ReleaseForm/AccountReceivableObservations';
import {
  useDocumentType,
  useDocumentTypeMutation,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import { useMemo } from 'react';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import CustomStatistic from '@/components/_common/CustomStatistic';
import useDefaultMinLengthNegativeFieldError from '@/hooks/useDefaultMinLengthNegativeFieldError';
import StatusTag from '@/components/_common/StatusTag';
import { useMatrixCustomerSettingsDetails } from '@/core/domain/enterprise/MatrixCustomerSettings/MatrixCustomerSettings.hooks';
import BankSlipDownload from '@/components/Financial/BankSlip/BankSlipDownload';
import BankSlipWhatsApp from '@/components/Financial/BankSlip/BankSlipWhatsApp';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props {
  form: FormInstance<AccountReceivableFormValues>;
  isEdit?: true;
  disabled?: boolean;
}

function AccountReceivableForm({ form, isEdit, disabled }: Props) {
  const { currentInstallment, accountReceivable, setUpdateTariff } =
    useAccountReceivableContext();

  const { data: customerMatrixSettings } = useMatrixCustomerSettingsDetails();

  const defaultMinLengthNegativeFieldError =
    useDefaultMinLengthNegativeFieldError();

  const intl = useIntl();
  const documentTypeIdField = Form.useWatch('documentTypeId', form);
  const formValues = Form.useWatch([], form);
  const { data: documentType, isLoading: documentTypeLoading } =
    useDocumentType(documentTypeIdField);

  const documentTypeDetail = useDocumentTypeMutation();

  const isStoneOrder = !!accountReceivable?.isStoneOrder;

  const hasInstallmentSettled = useMemo(
    () =>
      accountReceivable?.installmentReceivables.some(
        (item) => item.situation !== AccountReceivableSituation.open,
      ),
    [accountReceivable],
  );

  const installment =
    accountReceivable?.installmentReceivables[currentInstallment];

  const hasInstallmentReceivableBill =
    installment?.hasInstallmentReceivableBill;

  const oldPercentage = useMemo(() => {
    return (
      (installment?.rateValue || 0) /
      ((installment?.installmentValue || 0) +
        (installment?.discountIncrease || 0))
    );
  }, [installment]);

  const hasDocumentTypeInstallmentSettled = useMemo(
    () =>
      accountReceivable?.installmentReceivables.some(
        (item) =>
          item.situation === AccountReceivableSituation.settled &&
          (item.documentTypeId === documentTypeIdField ||
            item.documentTypeId === installment?.documentTypeId),
      ),
    [accountReceivable, documentTypeIdField, installment],
  );

  const hasDiffRateInstallment = useMemo(() => {
    return !!accountReceivable?.installmentReceivables.some(
      (receivableItem, _, items) =>
        items.some((installmentItem) => {
          const installmentTotal =
            installmentItem.rateValue +
            installmentItem.installmentValue +
            installmentItem.discountIncrease;

          const installmentTariff = (
            (installmentItem.rateValue / installmentTotal) *
            100
          ).toFixed(2);

          const receivableTotal =
            receivableItem.rateValue +
            receivableItem.installmentValue +
            receivableItem.discountIncrease;

          const receivableTariff = (
            (receivableItem.rateValue / receivableTotal) *
            100
          ).toFixed(2);

          return (
            installmentItem.documentTypeId === receivableItem.documentTypeId &&
            installmentTariff !== receivableTariff
          );
        }),
    );
  }, [installment, accountReceivable]);

  const currentPercentage = useMemo(() => {
    if (documentType?.fixedRate) {
      return (documentType.percentRate || 0) / 100;
    } else {
      const installmentSize = accountReceivable?.installmentReceivables?.filter(
        ({ documentTypeId }) => documentTypeId === documentTypeIdField,
      )?.length;

      if (typeof installmentSize === 'number') {
        const rate = documentType?.rates.find(
          (rate) => rate.numberInstallments === installmentSize,
        );

        return (rate?.percentRate || 0) / 100;
      }

      return 0;
    }
  }, [documentType, accountReceivable, documentTypeIdField]);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();

  const debtor = Form.useWatch('debtor', form);
  const installmentValue = Form.useWatch('installmentValue', form);
  const netValue = Form.useWatch('netValue', form);
  const amountReceivable = Form.useWatch('amountReceivable', form);
  const installmentNumber = Form.useWatch('installmentNumber', form);
  const discountIncrease = Form.useWatch('discountIncrease', form);

  const hasWorkorderId = !!accountReceivable?.workOrderId;
  const hasDirectSaleId = !!accountReceivable?.directSaleId;

  const hasDiffInstallmentValue = useMemo(() => {
    return (
      hasWorkorderId &&
      installmentValue?.toFixed(2) !== installment?.installmentValue?.toFixed(2)
    );
  }, [installmentValue, installment?.installmentValue, hasWorkorderId]);

  const enabledValueEdit = isEdit
    ? hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_EDIT_VALUE])
    : true;

  const getInstallmentRate = useGetInstallmentRate();

  const handleFormValuesChange = async (
    fieldValue: AccountReceivableFormValues,
    fieldValues: AccountReceivableFormValues,
  ) => {
    if (fieldValues.documentTypeId !== installment?.documentTypeId) {
      form.setFieldValue('updateTariff', true);
    }

    form.setFieldValue(
      'amountReceivable',
      (fieldValues.installmentValue ?? 0) + (fieldValues.discountIncrease ?? 0),
    );

    if (Object.keys(fieldValue).some((key) => key === 'debtor')) {
      form.setFieldsValue({
        customerId: undefined,
        customerName: undefined,
        insuerId: undefined,
        insurerName: undefined,
      });
    }

    if (fieldValue.documentTypeId && setUpdateTariff) {
      setUpdateTariff(false);
    }

    if (
      Object.keys(fieldValue).some((key) =>
        [
          'installmentValue',
          'discountIncrease',
          'documentTypeId',
          'installmentNumber',
        ].includes(key),
      )
    ) {
      const tariff =
        fieldValues.documentTypeId !==
        accountReceivable?.installmentReceivables[currentInstallment]
          .documentTypeId
          ? await getInstallmentRate({
              installmentValue: fieldValues.installmentValue ?? 0,
              discountIncrease: fieldValues.discountIncrease ?? 0,
              currentInstallment: currentInstallment,
              installmentReceivables: accountReceivable?.installmentReceivables,
              documentTypeId: fieldValues.documentTypeId,
              isEdit,
              installmentQuantity:
                accountReceivable?.installmentReceivables.filter(
                  (item) => item.documentTypeId === fieldValues.documentTypeId,
                ).length || fieldValues.installmentNumber,
            })
          : ((fieldValues.installmentValue || 0) +
              (fieldValues.discountIncrease || 0)) *
              oldPercentage || 0;

      form.setFieldValue('rateValue', tariff);

      form.setFieldValue(
        'netValue',
        (fieldValues.installmentValue ?? 0) +
          (fieldValues.discountIncrease ?? 0) -
          (tariff ?? 0),
      );
    }
  };

  const handleDocumentTypeChange = async (documentTypeId: number) => {
    const documentDetails = await documentTypeDetail.mutateAsync(
      documentTypeId,
    );
    if (documentDetails) {
      if (documentDetails.payOnTime) {
        form.setFieldValue('bankAccountId', documentDetails.bankAccountId);
      }
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onValuesChange={handleFormValuesChange}
      key={currentInstallment}
      name="accountReceivableGeneralForm"
    >
      <ContentWrapper
        style={{
          width: '100%',
          marginBottom: 20,
          padding: '1.5rem 2rem 1.25rem',
        }}
      >
        <Row gutter={[16, 9]}>
          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.company',
              })}
              name="companyId"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField
                disabled={hasInstallmentSettled || hasInstallmentReceivableBill}
                queryEnable
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.debtor',
              })}
              name="debtor"
              rules={[defaultEmptyFieldError]}
              initialValue={AccountReceivableDebtor.Costumer}
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                disabled={
                  disabled ||
                  hasInstallmentSettled ||
                  hasInstallmentReceivableBill ||
                  hasWorkorderId ||
                  hasDirectSaleId
                }
                options={[
                  {
                    value: AccountReceivableDebtor.Costumer,
                    label: intl.formatMessage({
                      id: 'release.form.customer',
                    }),
                  },
                  {
                    value: AccountReceivableDebtor.Insurer,
                    label: intl.formatMessage({
                      id: 'release.form.insurer',
                    }),
                  },
                ]}
              />
            </FloatingFormItem>
          </Col>

          {debtor === AccountReceivableDebtor.Insurer ? (
            <Col span={24} md={12} lg={8}>
              <Form.Item hidden name="insuerId">
                <Input />
              </Form.Item>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.insurer',
                })}
                name="insurerName"
                rules={
                  hasPermissions([Authorities.REGISTER_INSURER_VIEW])
                    ? [defaultEmptyFieldError]
                    : []
                }
              >
                <InsurerInputSearch
                  disabled
                  fieldNameId="insuerId"
                  onInsurerSelect={(insurer) =>
                    form.setFieldsValue({
                      insuerId: insurer.id,
                      insurerName: insurer.name,
                    })
                  }
                  buttonSearchDisabled={
                    disabled ||
                    hasInstallmentSettled ||
                    hasInstallmentReceivableBill ||
                    hasWorkorderId ||
                    hasDirectSaleId
                  }
                />
              </FloatingFormItem>
            </Col>
          ) : (
            <Col span={24} md={12} lg={8}>
              <Form.Item hidden name="customerId">
                <Input />
              </Form.Item>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.customer',
                })}
                name="customerName"
                rules={[defaultEmptyFieldError]}
              >
                <CustomerInputSearch
                  addonAfter={
                    accountReceivable?.customerCategory?.id &&
                    customerMatrixSettings?.showCategoryInAccountReceivable ? (
                      <StatusTag
                        color={accountReceivable?.customerCategory.color}
                        label={accountReceivable?.customerCategory.name}
                        title={accountReceivable?.customerCategory.name}
                        style={{ width: 100, display: 'block' }}
                        size="lg"
                      />
                    ) : undefined
                  }
                  fieldNameId="customerId"
                  disabled
                  onCustomerSelect={(customer) =>
                    form.setFieldsValue({
                      customerId: customer.id,
                      customerName: customer.name,
                    })
                  }
                  buttonSearchDisabled={
                    disabled ||
                    hasInstallmentSettled ||
                    hasInstallmentReceivableBill ||
                    hasWorkorderId ||
                    hasDirectSaleId
                  }
                  showCreateButton
                />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.planAccount',
              })}
              name="planAccountId"
              rules={[defaultEmptyFieldError]}
            >
              <PlanAccountsSelect
                disabled={disabled || hasInstallmentSettled}
                queryFilter={{
                  hierarchy: PlanAccountHierarchy.Analitical,
                  destination: PlanAccountDestination.Revenue,
                  active: true,
                }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={8}>
            <FloatingFormItem
              label={intl.formatMessage({
                id: 'release.form.costCenter',
              })}
              name="costCenterId"
            >
              <CostCenterInfiniteSelectField
                disabled={disabled || hasInstallmentSettled}
              />
            </FloatingFormItem>
          </Col>

          {isEdit && (
            <Col span={24} md={12} lg={8}>
              <FloatingFormItem
                label="Número de autorização"
                name="authorizationNumber"
              >
                <Input
                  disabled={disabled || !!accountReceivable?.transaction?.id}
                />
              </FloatingFormItem>
            </Col>
          )}
        </Row>
      </ContentWrapper>

      {isEdit && (
        <AccountReceivableHeader hasDiffRate={hasDiffRateInstallment} />
      )}

      <Form.Item hidden name="installmentId" noStyle />

      <Form.Item hidden name="amountReceivable" />

      {hasDocumentTypeInstallmentSettled &&
        installment?.documentTypeId !== documentTypeIdField && (
          <Alert
            style={{ marginBottom: 24 }}
            type="warning"
            message={<strong>Alteração de tarifa</strong>}
            description={
              <>
                <p>
                  Esse tipo de documento já póssui parcelas liquidadas,
                  modificando as parcelas será recalculado apenas as parcelas em
                  aberto gerando divergências nas tarífas. Para recalcular todas
                  as parcelas será necessário o estorno das parcelas já
                  liquidadas. Deseja prosseguir?
                </p>
                <small>Salve as alterações para ignorar o alerta</small>
              </>
            }
          />
        )}

      <Row gutter={[16, 9]}>
        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.description',
            })}
            name="description"
            rules={[defaultEmptyFieldError]}
          >
            <Input disabled={disabled || hasInstallmentReceivableBill} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.documentNumber',
            })}
            name="documentNumber"
            rules={[defaultMaxLenghtFieldError(255)]}
          >
            <Input disabled={disabled || hasInstallmentReceivableBill} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.documentType',
            })}
            name="documentTypeId"
            rules={[defaultEmptyFieldError]}
          >
            <DocumentTypesInfiniteSelectField
              onChange={handleDocumentTypeChange}
              disabled={
                disabled || isStoneOrder || hasInstallmentReceivableBill
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.issueDate',
            })}
            name="emissionDate"
            initialValue={moment()}
            rules={[defaultEmptyFieldError]}
          >
            <InputDate disabled={disabled || hasInstallmentReceivableBill} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={isEdit ? 6 : 3}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.dueDate',
            })}
            name="dueDate"
            rules={[defaultEmptyFieldError]}
          >
            <InputDate disabled={disabled || hasInstallmentReceivableBill} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={isEdit ? 6 : 3}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.payDay',
            })}
            name="payDay"
          >
            <InputDate disabled={disabled || hasInstallmentReceivableBill} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={3} hidden={isEdit}>
          <FloatingFormItem
            label="Qtd. Parcelas"
            name="installmentNumber"
            initialValue={1}
          >
            <InputDecimal min={0} fractions={0} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={3} hidden={isEdit}>
          <FloatingFormItem
            label="Intervalo (dias)"
            name="intervalDays"
            initialValue={30}
          >
            <InputDecimal min={0} fractions={0} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={12}>
          <FloatingFormItem
            name="bankAccountId"
            label={intl.formatMessage({
              id: 'release.form.bankAccount',
            })}
          >
            <BankAccountInfiniteSelectField
              disabled={
                disabled ||
                hasInstallmentReceivableBill ||
                !!documentTypeDetail.data?.bankAccountId
              }
              queryFilter={{ active: true }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.value',
            })}
            name="installmentValue"
            rules={[
              defaultEmptyFieldError,
              {
                type: 'number',
                min: 0.01,
                message: 'Valor deve ser maior que R$ 0,00',
              },
            ]}
            initialValue={0}
          >
            <InputDecimal
              min={0.01}
              disabled={
                disabled || hasInstallmentReceivableBill || !enabledValueEdit
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label="(R$) Acres/Desc"
            name="discountIncrease"
            initialValue={0}
            rules={[defaultMinLengthNegativeFieldError(installmentValue * -1)]}
          >
            <InputDecimal disabled={disabled || hasInstallmentReceivableBill} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label="(R$) Tarifa"
            name="rateValue"
            initialValue={0}
          >
            <InputDecimal disabled />
          </FloatingFormItem>
        </Col>

        <Col
          span={24}
          md={12}
          lg={6}
          hidden={
            !installment ||
            installment?.documentTypeId !== documentTypeIdField ||
            installment?.situation === AccountReceivableSituation.settled ||
            oldPercentage.toFixed(4) === currentPercentage.toFixed(4) ||
            documentTypeLoading ||
            hasInstallmentSettled ||
            hasInstallmentReceivableBill
          }
        >
          <Group style={{ width: '100%' }} gap={10}>
            <Button
              block
              size="large"
              onClick={async () => {
                const tariff = await getInstallmentRate({
                  installmentValue: formValues.installmentValue ?? 0,
                  discountIncrease: formValues.discountIncrease ?? 0,
                  currentInstallment: currentInstallment,
                  installmentReceivables:
                    accountReceivable?.installmentReceivables,
                  documentTypeId: formValues?.documentTypeId,
                  isEdit,
                  installmentQuantity:
                    accountReceivable?.installmentReceivables.filter(
                      (item) =>
                        item.documentTypeId === formValues?.documentTypeId,
                    ).length,
                });

                form.setFieldValue('rateValue', tariff);

                form.setFieldValue(
                  'netValue',
                  (formValues.installmentValue ?? 0) +
                    (formValues.discountIncrease ?? 0) -
                    (tariff ?? 0),
                );

                if (setUpdateTariff) {
                  setUpdateTariff(true);
                }
              }}
            >
              Atualizar tarifa
            </Button>

            <HelpCircle
              style={{ cursor: 'pointer' }}
              onClick={() =>
                Modal.info({
                  title: (
                    <Space direction="vertical">
                      <Typography.Text>
                        <strong>Atualizar tarifa:</strong> utiliza a taxa atual
                        do tipo de documento.
                      </Typography.Text>
                    </Space>
                  ),
                })
              }
            />
          </Group>
        </Col>

        {!!formValues?.amountAdjusted && (
          <Col span={24} md={12} lg={6}>
            <Group style={{ width: '100%' }} gap={10}>
              <FloatingFormItem
                label="Valor ajustado"
                name="amountAdjusted"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>

              <HelpCircle
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  Modal.info({
                    title: (
                      <Space direction="vertical">
                        <Typography.Text>
                          Campo referente a valores que o cliente pagou de
                          diferença no boleto: Juros, Multa, Descontos, etc.
                        </Typography.Text>
                      </Space>
                    ),
                  })
                }
              />
            </Group>
          </Col>
        )}

        <Col span={24} md={12} lg={6} hidden>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'release.form.netValue',
            })}
            name="netValue"
            initialValue={0}
          >
            <InputDecimal disabled />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Group gap={18}>
            <CustomStatistic
              title="Total pago pelo cliente"
              description="totalPagoPeloCliente = valorParcela + descontoAcrescimo"
              value={amountReceivable}
              valueStyle={{ color: '#148e28' }}
            />

            <CustomStatistic
              title="Líquido a receber"
              description="valorLiquido = (valorParcela + descontoAcrescimo) - tarifas"
              value={netValue}
              valueStyle={{ color: '#4bbacf' }}
            />

            {!isEdit && (
              <Statistic
                title="Valor das parcelas"
                value={`${installmentNumber || 1}x ${formatterPrice(
                  (installmentValue || 0) / (installmentNumber || 1) +
                    (discountIncrease || 0) / (installmentNumber || 1),
                )}`}
              />
            )}
          </Group>
        </Col>

        {installment?.hasInstallmentReceivableBill && !!installment?.billId && (
          <Col span={24}>
            <Group gap={12} align="center">
              <Group gap={10}>
                <CheckSquare color="green" />

                <Typography.Title
                  level={4}
                  style={{ margin: 0, whiteSpace: 'nowrap', lineHeight: 1 }}
                >
                  Boleto Gerado
                </Typography.Title>
              </Group>

              <Divider type="vertical" />

              <SpaceForButton>
                <BankSlipDownload
                  bankSlip={{
                    id: installment?.billId,
                    ourNumber: installment.billOurNumber,
                    printProtocol: installment.billPrintProtocol,
                  }}
                  show={!!installment?.billId}
                  type="button"
                />

                <BankSlipWhatsApp
                  bankSlip={{
                    id: installment.billId,
                    printProtocol: installment.billPrintProtocol,
                    customerId: accountReceivable?.customerId,
                  }}
                  show={!!installment?.billId}
                  type="button"
                />
              </SpaceForButton>
            </Group>
          </Col>
        )}

        {hasDiffInstallmentValue && (
          <Col span={24}>
            <Alert
              type="warning"
              description="A alteração do valor pode impactar no resumo financeiro do orçamento relacionado."
            />
          </Col>
        )}

        {isEdit && (
          <>
            <Divider />

            <Col span={24}>
              <AccountReceivableObservations />
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
}

export default AccountReceivableForm;
